

/**
 * Developer Configuration
 *
 * This file contains developer-only overrides for testing purposes.
 * These overrides should only be active in development mode.
 */

import { User } from "@supabase/supabase-js";

// Developer override to bypass authentication and grant Pro access
export const developerOverride = (() => {
  if (typeof window !== "undefined") {
    const param = new URLSearchParams(window.location.search).get("dev");
    const localFlag = localStorage.getItem("devOverride");
    const env = import.meta.env.MODE;

    const isDevMode = param === "true" || localFlag === "true";
    const result = env !== "production" && isDevMode;

    console.log(`[Config] developerOverride is set to: ${result}`);
    return result;
  }

  return false;
})();

/**
 * A valid mock user ID used in development to simulate authentication.
 * This must be a valid UUID to prevent Supabase/PostgreSQL errors.
 */
export const mockUserId = "11111111-1111-1111-1111-111111111111";

/**
 * Check if the current user should be treated as a Pro user
 * @param user - The authenticated user (can be null)
 * @param userProfile - The user's profile data (can be null)
 * @returns boolean indicating if user has Pro access
 */
export const isProUser = (user: any = null, userProfile: any = null): boolean => {
  if (developerOverride) {
    console.log("[DEV OVERRIDE] Treating user as Pro user");
    return true;
  }

  return !!(user && userProfile);
};

/**
 * Check if the current user should be treated as authenticated
 * @param user - The authenticated user (can be null)
 * @returns boolean indicating if user is authenticated
 */
export const isAuthenticatedUser = (user: any = null): boolean => {
  if (developerOverride) {
    console.log("[DEV OVERRIDE] Treating user as authenticated");
    return true;
  }

  return !!user;
};

/**
 * Return a mock authenticated user for development
 * @returns a mock user object with a valid UUID and full User shape
 */
export const getMockUser = (): User | null => {
  if (!developerOverride) return null;

  const now = new Date().toISOString();

  return {
    id: mockUserId,
    aud: "authenticated",
    role: "authenticated",
    email: "dev@example.com",
    email_confirmed_at: now,
    phone: "",
    confirmed_at: now,
    last_sign_in_at: now,
    created_at: now,
    updated_at: now,
    app_metadata: {
      provider: "email",
      providers: ["email"]
    },
    user_metadata: {
      full_name: "Developer User"
    },
    identities: []
  };
};

